import { forwardRef, useRef, useCallback, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { comboboxVirtualListStyles } from './combobox.styles';
import { ComboboxItem } from './combobox-item';
import type { ComboboxVirtualListProps } from './combobox.types';

export const ComboboxVirtualList = forwardRef(
  <TValue extends string | number = string>(
    {
      options,
      selectedValues,
      focusedIndex,
      onSelect,
      itemHeight = 40,
      containerHeight = 300,
      overscan = 5,
      size = 'md',
      renderOption,
      onScrollToBottom,
      className,
    }: ComboboxVirtualListProps<TValue>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const parentRef = useRef<HTMLDivElement>(null);

    // Initialize virtualizer
    const virtualizer = useVirtualizer({
      count: options.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => itemHeight,
      overscan,
    });

    // Scroll to focused item when focusedIndex changes
    useEffect(() => {
      if (focusedIndex >= 0 && focusedIndex < options.length) {
        virtualizer.scrollToIndex(focusedIndex, {
          align: 'auto',
        });
      }
    }, [focusedIndex, options.length, virtualizer]);

    // Detect scroll to bottom for infinite scroll
    const handleScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        if (!onScrollToBottom) return;

        const target = e.currentTarget;
        const threshold = 50; // pixels from bottom
        const isNearBottom =
          target.scrollHeight - target.scrollTop - target.clientHeight < threshold;

        if (isNearBottom) {
          onScrollToBottom();
        }
      },
      [onScrollToBottom]
    );

    const virtualItems = virtualizer.getVirtualItems();

    return (
      <div
        ref={(node) => {
          parentRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={comboboxVirtualListStyles({ size, className })}
        style={{ height: `${containerHeight}px`, overflow: 'auto' }}
        onScroll={handleScroll}
        role="listbox"
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map((virtualItem) => {
            const option = options[virtualItem.index];
            if (!option) return null;

            const isSelected = selectedValues.includes(option.value);
            const isFocused = focusedIndex === virtualItem.index;

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {renderOption ? (
                  renderOption(option, {
                    isSelected,
                    isFocused,
                    isDisabled: option.disabled || false,
                    isHighlighted: false,
                  })
                ) : (
                  <ComboboxItem
                    option={option}
                    isSelected={isSelected}
                    isFocused={isFocused}
                    isDisabled={option.disabled}
                    onSelect={() => onSelect(option)}
                    size={size}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
) as (<TValue extends string | number = string>(
  props: ComboboxVirtualListProps<TValue> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement) & { displayName?: string };

ComboboxVirtualList.displayName = 'ComboboxVirtualList';
